import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import Modal from '@/components/ui/Modal'
import ProcessTemplateSelector from '@/features/macro-process-creation/components/ProcessTemplateSelector'
import useFormConfirmation from '@/hooks/useFormConfirmation.jsx'
import { addFlashMessage } from '@/features/flash/flashMessagesSlice'
import {
  closeEditModal,
  toggleTemplate,
  setTemplatePosition,
  resetRequestStatus,
  selectContainersByTemplateId,
} from '../macroProcessEditSlice'
import { addContainersToMacroProcess } from '../services/macroProcessEditService'

export default function MacroProcessEditModal() {
  const dispatch = useDispatch()
  const modalRef = useRef(null)

  const {
    modalOpen,
    activeMacroProcess,
    availableTemplates,
    selectedTemplates,
    loadingStatus,
    requestStatus,
  } = useSelector(state => state.macroProcessEdit)

  const containersByTemplateId = useSelector(selectContainersByTemplateId)

  const errors = requestStatus?.errors
  const isLoading = loadingStatus.addContainers === 'pending'
  const isLoadingTemplates = loadingStatus.fetchTemplates === 'pending'

  const { handleSubmit, reset, getValues, watch, formState: { errors: formErrors } } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  // Ouverture/fermeture du dialog natif
  useEffect(() => {
    const dialog = modalRef.current
    if (!dialog) return
    if (modalOpen) {
      if (!dialog.open) dialog.showModal()
    } else {
      if (dialog.open) dialog.close()
    }
  }, [modalOpen])

  // Flash message de succès (le modal est déjà fermé par le slice)
  useEffect(() => {
    if (requestStatus.success) {
      dispatch(addFlashMessage({ type: 'success', text: 'Étapes ajoutées avec succès' }))
      dispatch(resetRequestStatus())
    }
  }, [requestStatus.success, dispatch])

  const onReset = () => {
    reset()
    dispatch(resetRequestStatus())
  }

  const onClose = () => {
    dispatch(closeEditModal())
  }

  const {
    handleFormCancel,
    FormConfirmDialog,
  } = useFormConfirmation({
    watch,
    getValues,
    onReset,
    onClose,
  })

  const onSubmit = async (formData) => {
    if (selectedTemplates.length === 0) return

    const action = await dispatch(addContainersToMacroProcess({
      macroProcessId: activeMacroProcess.id,
      templatePositions: selectedTemplates.map(t => ({
        template_id: t.templateId,
        position: t.position,
      })),
    }))

    if(addContainersToMacroProcess.rejected.match(action)) {
    }
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) => {
    const errorsMsg = formErrors?.[fieldName]?.message || errors?.[fieldName] || ''
    if (Array.isArray(errorsMsg)) {
      return errorsMsg.join(', ')
    }
    return errorsMsg
  }

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean}
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  const isTemplateDisabled = (templateId) =>
    containersByTemplateId[templateId]?.status === 'pending'

  return (
    <Modal ref={modalRef} backdrop={false} onClose={onClose} className="max-w-lg">
      <Modal.Header showCloseButton={false} className="mb-4">
        Ajouter des étapes au process
      </Modal.Header>

      <Modal.Body className="overflow-y-auto min-h-[400px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          {activeMacroProcess && (
            <p className="text-sm text-base-content/60 mb-4">
              Process : <strong>{activeMacroProcess.name}</strong>
            </p>
          )}

          <div className="card bg-base-100 border border-base-200 rounded-none mb-4">
            <div className="p-2 min-h-[400px]">
              <p className="label-text font-medium mb-2">Sélectionner les templates</p>
              <div className="max-h-[calc(80vh-150px)] overflow-y-auto">
                {isLoadingTemplates ? (
                  <div className="flex items-center gap-2 py-4">
                    <span className="loading loading-spinner loading-sm" />
                    <span className="text-sm text-base-content/60">Chargement des templates…</span>
                  </div>
                ) : (
                  <>
                    {hasError('position') && (
                      <p className="text-error text-sm mt-2">{getErrorMessage('position')}</p>
                    )}
                    <ProcessTemplateSelector
                      templates={availableTemplates}
                      isSelected={(id) => selectedTemplates.some(t => t.templateId === id)}
                      getPosition={(id) => {
                        const selected = selectedTemplates.find(t => t.templateId === id)
                        if (selected) return selected.position
                        return containersByTemplateId[id]?.position ?? ''
                      }}
                      onToggle={(id) => dispatch(toggleTemplate(id))}
                      onPositionChange={(id, position) => dispatch(setTemplatePosition({ templateId: id, position }))}
                      isDisabled={isTemplateDisabled}
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="modal-action justify-between">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleFormCancel}
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || isLoadingTemplates || selectedTemplates.length === 0}
            >
              {isLoading && <span className="loading loading-spinner loading-xs mr-1" />}
              Ajouter
            </button>
          </div>
        </form>
      </Modal.Body>
      <FormConfirmDialog />
    </Modal>
  )
}
